/**
 * Lists mode of the Pages editor — the record lists (Drip menu, Services,
 * FAQs, Program steps, Testimonials) in the same takeover as pages.
 *
 * Same three panes as a page, same components: left = the list's items
 * in site order (drag to reorder, eye to show/hide, ⋯ duplicate/delete,
 * + Add); center = the live page that renders the list (PagePreview in
 * record mode: edits patch live, reorders and hides apply live — and the
 * WHOLE preview stays clickable: a card of this list opens it, a card of
 * another list on the page switches to that list, anything else opens
 * its section on the page); right = the item's form, GENERATED from
 * RECORD_SCHEMAS via formgen's SchemaForm, autosaved by useLists.
 *
 * Grouped lists (FAQs, steps) split into their page groups; each group
 * previews on the page whose section shows it. Where a list shows is read
 * from the page docs (record-bound sections), not hard-coded.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LIST_KEYS,
  LIST_META,
  RECORD_SCHEMAS,
  listForSection,
  type ListKey,
} from '@vv/sections';
import PagePreview from '~/components/PagePreview';
import { SchemaForm, type FieldMeta } from '~/lib/formgen';
import { recordPatches, sectionPatches } from '~/lib/previewPatches';
import {
  LIST_DEFS,
  groupFieldMeta,
  groupOf,
  isGrouped,
  recKey,
  type ListRecord,
} from '~/lib/lists';
import type { ListsApi } from '~/lib/useLists';
import type { PageDoc } from '~/lib/usePageDocs';

interface Placement {
  slug: string;
  title: string;
  group?: string;
}

function pageTitle(doc: PageDoc): string {
  return (doc.meta.title ?? doc.slug).split('—')[0]!.trim();
}

export function hrefForSlug(slug: string): string {
  return slug === 'home' ? '/' : `/${slug}`;
}

function slugForHref(href: string): string {
  return href === '/' ? 'home' : href.slice(1);
}

/** Pages whose (visible) sections render this list — one entry per
 * page + group. */
export function placementsFor(list: ListKey, docs: Record<string, PageDoc>): Placement[] {
  const out: Placement[] = [];
  const seen = new Set<string>();
  for (const doc of Object.values(docs)) {
    for (const s of doc.sections) {
      if (s.data.hidden === true) continue;
      const hit = listForSection(s.data);
      if (!hit || hit.list !== list) continue;
      const id = `${doc.slug}|${hit.group ?? ''}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ slug: doc.slug, title: pageTitle(doc), group: hit.group });
    }
  }
  return out;
}

/** Where "View live" and a fresh preview land for a list. */
export function defaultListHref(list: ListKey, docs: Record<string, PageDoc>): string {
  const places = placementsFor(list, docs);
  const fallback = LIST_DEFS[list].fallbackHref;
  const preferred = places.find((p) => hrefForSlug(p.slug) === fallback);
  const pick = preferred ?? places[0];
  return pick ? hrefForSlug(pick.slug) : fallback;
}

const EyeIcon = ({ off }: { off: boolean }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const chipStyle: React.CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '2px 6px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

export default function ListWorkspace({
  list,
  lists,
  docs,
  selectedId,
  onSelect,
  onOpenList,
  onOpenSection,
  onNavigate,
  onOpenChrome,
}: {
  list: ListKey;
  lists: ListsApi;
  docs: Record<string, PageDoc>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** A card of ANOTHER list was clicked in the preview — open it there. */
  onOpenList: (list: ListKey, id: string) => void;
  /** A non-card part of a section was clicked — open that section on its
   * page (`target` = the deployed data-section id, `field` = its data-f). */
  onOpenSection: (slug: string, target: string, field?: string) => void;
  /** A nav/footer link was clicked in the preview — open that page. */
  onNavigate: (path: string) => void;
  /** Nav/footer (not a link) was clicked — open the Nav & footer editor. */
  onOpenChrome: () => void;
}) {
  const def = LIST_DEFS[list];
  const meta = LIST_META[list];
  const grouped = isGrouped(list);
  const rows = lists.records[list];
  const loading = lists.loadingLists.has(list);
  const loadError = lists.loadErrors[list];

  const [hoverId, setHoverId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragPos, setDragPos] = useState<'above' | 'below'>('above');
  const [focusPath, setFocusPath] = useState<{ path: string; nonce: number } | null>(null);
  const focusSeq = useRef(0);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  /** Records in the deployed build (from the preview's ready handshake),
   * per previewed page — the iframe survives switching between two lists
   * that preview on the same page, so this must too. */
  const [inventoryByHref, setInventoryByHref] = useState<{ href: string; recs: Set<string> } | null>(
    null,
  );
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);

  const { ensureLoaded, loadedRecord } = lists;
  useEffect(() => {
    void ensureLoaded(list);
  }, [list, ensureLoaded]);

  // A fresh list starts with no chosen preview page / field focus.
  useEffect(() => {
    setPreviewSlug(null);
    setFocusPath(null);
    setMenuId(null);
    setActionError(null);
  }, [list]);

  // Row ⋯ menu closes on any outside click.
  useEffect(() => {
    if (!menuId) return;
    const close = () => setMenuId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuId]);

  const placements = useMemo(() => placementsFor(list, docs), [list, docs]);
  const selected = rows?.find((r) => r.id === selectedId) ?? null;

  // Keep the selected row visible when selection arrives from the preview.
  useEffect(() => {
    if (selectedId) rowRefs.current.get(selectedId)?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // ── Groups ──────────────────────────────────────────────────────
  const groupLabel = (g: string): string => {
    const doc = docs[g];
    return doc ? `${pageTitle(doc)} page` : g;
  };
  const groups = useMemo(() => {
    const all = rows ?? [];
    if (!grouped) return [{ key: '', records: all }];
    const keys: string[] = [];
    for (const p of placements) if (p.group && !keys.includes(p.group)) keys.push(p.group);
    for (const r of all) {
      const g = groupOf(list, r);
      if (!keys.includes(g)) keys.push(g);
    }
    // An empty grouped list still needs one group to add into.
    if (keys.length === 0) keys.push(String(def.createBody(undefined, []).page ?? ''));
    return keys.map((key) => ({ key, records: all.filter((r) => groupOf(list, r) === key) }));
  }, [rows, grouped, placements, list, def]);

  const fieldMeta = useMemo<Record<string, FieldMeta>>(() => {
    if (!grouped) return def.fieldMeta;
    const keys = new Set<string>([...groups.map((g) => g.key), ...Object.keys(docs)]);
    const options = [...keys].filter(Boolean).map((k) => ({ value: k, label: groupLabel(k) }));
    return { ...def.fieldMeta, page: groupFieldMeta(options, meta.itemLabel) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, grouped, groups, docs, meta.itemLabel]);

  // ── Preview target ──────────────────────────────────────────────
  const previewHref = useMemo(() => {
    if (grouped) {
      const g = selected ? groupOf(list, selected) : (groups[0]?.key ?? '');
      const p = placements.find((x) => x.group === g);
      if (p) return hrefForSlug(p.slug);
    }
    if (previewSlug && placements.some((p) => p.slug === previewSlug)) return hrefForSlug(previewSlug);
    return defaultListHref(list, docs);
  }, [grouped, selected, groups, placements, previewSlug, list, docs]);

  /** This page's deployed records, once its preview has handshaken. */
  const inventory = inventoryByHref?.href === previewHref ? inventoryByHref.recs : null;

  // Everything unpublished, not just this list: every loaded list's cards
  // (a page carries several lists) and the previewed page's section drafts.
  // Otherwise the preview only ever showed the edits of whatever was open,
  // and switching between a card and its section made the other's disappear.
  const { recordFields, recordOrder } = useMemo(
    () => recordPatches(lists),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lists.records, loadedRecord],
  );
  const { sectionFields, sectionOrder } = useMemo(
    () => sectionPatches(docs[slugForHref(previewHref)]),
    [docs, previewHref],
  );

  const selectedSelector = selected ? `[data-rec="${recKey(list, selected.id)}"]` : null;
  const hoverSelector = hoverId ? `[data-rec="${recKey(list, hoverId)}"]` : null;

  function selectRecord(id: string, field?: string) {
    onSelect(id);
    setMenuId(null);
    if (field) {
      focusSeq.current += 1;
      setFocusPath({ path: def.previewFieldFor?.[field] ?? field, nonce: focusSeq.current });
    } else {
      setFocusPath(null);
    }
  }

  function previewNotice(): React.ReactNode {
    if (!selected || !inventory) return null;
    const r = selected;
    if (grouped && !placements.some((p) => p.group === groupOf(list, r))) {
      return <>No page shows the “{groupLabel(groupOf(list, r))}” group yet.</>;
    }
    if (inventory.has(recKey(list, r.id))) {
      // On the page — but some edits can't patch live (badges, icons,
      // links, and leaves the build only renders when set). Say so, or it
      // reads like the edit didn't save.
      const loaded = loadedRecord(list, r.id);
      if (!loaded) return null;
      const same = (k: string) => JSON.stringify(r[k] ?? null) === JSON.stringify(loaded[k] ?? null);
      const empty = (v: unknown) =>
        v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
      const later = [
        ...(def.publishOnlyFields ?? []).filter((k) => !same(k)),
        ...(def.conditionalFields ?? []).filter((k) => !same(k) && empty(loaded[k])),
      ];
      if (later.length === 0) return null;
      return (
        <>
          Saved. {later.map((k) => def.fieldMeta[k]?.label ?? k).join(', ')}{' '}
          {later.length === 1 ? 'shows' : 'show'} in the preview after the next{' '}
          <strong>Publish</strong>.
        </>
      );
    }
    if (def.onPage && !def.onPage(r)) return <>{def.offPageNote}</>;
    if (!r.is_active) {
      return (
        <>
          This {meta.itemLabel} is hidden. Turn on <strong>Shown on site</strong>, then Publish.
        </>
      );
    }
    return (
      <>
        This {meta.itemLabel} isn't on the live site yet — it shows after the next{' '}
        <strong>Publish</strong>.
      </>
    );
  }

  // ── Actions ─────────────────────────────────────────────────────
  async function add(group: string) {
    setBusy(`add:${group}`);
    setActionError(null);
    const r = await lists.create(list, grouped ? group : undefined);
    setBusy(null);
    if (r.ok) selectRecord(r.id);
    else setActionError(r.error);
  }

  async function duplicate(id: string) {
    setBusy(`dup:${id}`);
    setActionError(null);
    const r = await lists.duplicate(list, id);
    setBusy(null);
    if (r.ok) selectRecord(r.id);
    else setActionError(r.error);
  }

  async function remove(r: ListRecord) {
    if (!window.confirm(`Delete “${def.rowTitle(r)}”? This can't be undone.`)) return;
    setActionError(null);
    const ok = await lists.remove(list, r.id);
    if (!ok) setActionError(`Couldn't delete “${def.rowTitle(r)}” — try again.`);
    else if (selectedId === r.id) onSelect(null);
  }

  function drop(target: ListRecord, groupRecords: ListRecord[]) {
    if (!drag || drag === target.id) return;
    const ids = groupRecords.map((r) => r.id);
    if (!ids.includes(drag)) return; // rows only move within their group
    const without = ids.filter((id) => id !== drag);
    let at = without.indexOf(target.id) + (dragPos === 'below' ? 1 : 0);
    if (at < 0) at = without.length;
    without.splice(at, 0, drag);
    lists.reorder(list, grouped ? groupOf(list, target) : '', without);
  }

  // ── Rail row ────────────────────────────────────────────────────
  function renderRow(r: ListRecord, groupRecords: ListRecord[]) {
    const key = recKey(list, r.id);
    const isSelected = r.id === selectedId;
    const hidden = !r.is_active;
    const offPage = def.onPage ? !def.onPage(r) : false;
    const notLive = inventory !== null && r.is_active && !offPage && !inventory.has(key);
    const blocked = (lists.issues[key]?.length ?? 0) > 0;
    const chip = def.rowChip?.(r);
    const isDragOver = dragOver === r.id;
    return (
      <div
        key={r.id}
        ref={(el) => {
          if (el) rowRefs.current.set(r.id, el);
          else rowRefs.current.delete(r.id);
        }}
        draggable
        onDragStart={(e) => {
          setDrag(r.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', r.id);
        }}
        onDragOver={(e) => {
          if (!drag || drag === r.id || !groupRecords.some((x) => x.id === drag)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const rect = e.currentTarget.getBoundingClientRect();
          setDragOver(r.id);
          setDragPos(e.clientY < rect.top + rect.height / 2 ? 'above' : 'below');
        }}
        onDragLeave={() => {
          if (dragOver === r.id) setDragOver(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          drop(r, groupRecords);
          setDrag(null);
          setDragOver(null);
        }}
        onDragEnd={() => {
          setDrag(null);
          setDragOver(null);
        }}
        onClick={() => selectRecord(r.id)}
        onMouseEnter={() => setHoverId(r.id)}
        onMouseLeave={() => setHoverId((cur) => (cur === r.id ? null : cur))}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 8,
          border: isSelected ? '1px solid var(--teal)' : '1px solid var(--line-soft)',
          // Drop indicator as an inset shadow — toggling borderTop/Bottom
          // next to the `border` shorthand makes React warn on every drag.
          boxShadow:
            isDragOver && dragPos === 'above'
              ? 'inset 0 2px 0 var(--teal)'
              : isDragOver && dragPos === 'below'
                ? 'inset 0 -2px 0 var(--teal)'
                : undefined,
          background: isSelected ? 'rgba(95,168,162,0.12)' : 'var(--card)',
          opacity: drag === r.id ? 0.35 : hidden ? 0.55 : 1,
          cursor: 'pointer',
          userSelect: 'none',
          fontSize: 13.5,
          fontWeight: isSelected ? 600 : 400,
          transition: 'background 120ms, border-color 120ms, opacity 120ms',
        }}
      >
        <span style={{ cursor: 'grab', opacity: 0.5 }} aria-hidden title="Drag to reorder">
          ⠿
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {def.rowTitle(r)}
          </span>
          <span
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              fontSize: 11.5,
              color: 'var(--text-muted)',
              fontWeight: 400,
              minWidth: 0,
            }}
          >
            {chip && (
              <span style={{ ...chipStyle, background: 'var(--mint)', color: 'var(--primary)' }}>{chip}</span>
            )}
            {offPage && (
              <span style={{ ...chipStyle, background: 'var(--surface)', color: 'var(--text-muted)' }} title={def.offPageNote}>
                Booking only
              </span>
            )}
            {notLive && (
              <span
                style={{ ...chipStyle, color: 'var(--teal)', padding: 0 }}
                title="Not in the live site yet — shows after the next Publish"
              >
                New
              </span>
            )}
            {blocked && (
              <span style={{ ...chipStyle, color: '#c44545', padding: 0 }} title="Not saving — fix the fields flagged in the panel">
                ⚠ Fix
              </span>
            )}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {def.rowSub(r)}
            </span>
          </span>
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            lists.toggleActive(list, r.id);
          }}
          title={hidden ? 'Hidden — click to show on the site' : 'Shown on the site — click to hide'}
          aria-label={hidden ? `Show ${def.rowTitle(r)} on the site` : `Hide ${def.rowTitle(r)} from the site`}
          aria-pressed={!hidden}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: hidden ? 'var(--text-muted)' : 'var(--teal)',
            display: 'inline-flex',
            padding: 2,
            borderRadius: 4,
          }}
        >
          <EyeIcon off={hidden} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuId((cur) => (cur === r.id ? null : r.id));
          }}
          aria-label={`Actions for ${def.rowTitle(r)}`}
          aria-expanded={menuId === r.id}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: 15,
            lineHeight: 1,
            padding: '2px 4px',
            borderRadius: 4,
          }}
        >
          ⋯
        </button>
        {menuId === r.id && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              right: 8,
              top: '100%',
              zIndex: 30,
              minWidth: 150,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: 4,
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              boxShadow: 'var(--sh-2)',
            }}
          >
            {[
              { label: 'Duplicate', onPick: () => void duplicate(r.id) },
              { label: hidden ? 'Show on site' : 'Hide from site', onPick: () => lists.toggleActive(list, r.id) },
              { label: 'Delete…', danger: true, onPick: () => void remove(r) },
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  setMenuId(null);
                  item.onPick();
                }}
                style={{
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  color: item.danger ? '#c44545' : 'var(--text)',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Props panel ─────────────────────────────────────────────────
  function renderPanel() {
    if (!selected) return null;
    const key = recKey(list, selected.id);
    const issues = lists.issues[key] ?? [];
    const group = grouped ? groupOf(list, selected) : '';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid var(--line-soft)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 17,
                fontWeight: 700,
                color: 'var(--primary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {def.rowTitle(selected)}
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              {meta.label}
              {group ? ` · ${groupLabel(group)}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            title="Close (Esc)"
            style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            ×
          </button>
        </div>
        {issues.length > 0 && (
          <div
            style={{
              margin: '10px 14px 0',
              padding: '8px 12px',
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.3)',
              borderRadius: 8,
              fontSize: 12,
              color: '#c44545',
            }}
          >
            <strong>Not saving yet — fix:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {issues.slice(0, 5).map((iss, i) => (
                <li key={i}>
                  {def.fieldMeta[iss.path]?.label ?? (iss.path || 'item')}: {iss.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SchemaForm
            // Keyed per record so switching items remounts the form (no
            // ItemList / money-input state leaking between records).
            key={selected.id}
            schema={RECORD_SCHEMAS[list]}
            value={selected}
            onChange={(next) => lists.setRecord(list, selected.id, next)}
            focusPath={focusPath}
            fieldMeta={fieldMeta}
            skip={['is_active']}
            plainText
          />
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selected.is_active}
              onChange={() => lists.toggleActive(list, selected.id)}
            />
            Shown on site
          </label>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>
            Saves automatically. Hit Publish (top right) to put changes live.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => void duplicate(selected.id)}
              disabled={busy !== null}
              className="content-editor__rebuild"
              style={{ flex: 1 }}
            >
              {busy === `dup:${selected.id}` ? 'Duplicating…' : 'Duplicate'}
            </button>
            <button
              type="button"
              onClick={() => void remove(selected)}
              style={{
                flex: 1,
                background: 'transparent',
                border: '1px solid rgba(196,69,69,0.4)',
                color: '#c44545',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────
  const showPreviewPicker = placements.length > 1 && !grouped;
  return (
    <div className={`pages-editor__body${selected ? ' has-selection' : ''}`}>
      <nav className="pages-editor__nav">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ padding: '2px 2px 4px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>
              {meta.label}
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{meta.hint}</p>
            {showPreviewPicker && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                Preview on
                <select
                  value={placements.find((p) => hrefForSlug(p.slug) === previewHref)?.slug ?? ''}
                  onChange={(e) => setPreviewSlug(e.target.value)}
                  style={{ fontSize: 12, padding: '3px 6px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--card)' }}
                >
                  {placements.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {loadError && (
            <div className="content-editor__error" style={{ padding: '8px 12px' }}>
              Couldn't load {meta.label}: {loadError}{' '}
              <button type="button" onClick={() => void lists.ensureLoaded(list, true)} style={{ marginLeft: 6 }}>
                Retry
              </button>
            </div>
          )}
          {actionError && (
            <div className="content-editor__error" style={{ padding: '8px 12px' }}>
              {actionError}
            </div>
          )}
          {loading && !rows && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 2px' }}>Loading…</p>
          )}

          {rows &&
            groups.map((g) => (
              <div key={g.key || 'all'} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {grouped && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      margin: '6px 2px 0',
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span>{groupLabel(g.key)}</span>
                    {!placements.some((p) => p.group === g.key) && (
                      <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: 'none' }} title="No FAQ/Steps section shows this group yet">
                        not on a page
                      </span>
                    )}
                  </div>
                )}
                {g.records.map((r) => renderRow(r, g.records))}
                <button
                  type="button"
                  onClick={() => void add(g.key)}
                  disabled={busy !== null}
                  style={{
                    background: 'transparent',
                    border: '1px dashed var(--line)',
                    color: 'var(--teal)',
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 12.5,
                    cursor: busy ? 'wait' : 'pointer',
                  }}
                >
                  {busy === `add:${g.key}` ? 'Adding…' : `+ Add ${meta.itemLabel}`}
                </button>
              </div>
            ))}
          {rows && (
            <p style={{ margin: '4px 2px 0', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>
              New {meta.itemLabel}s start hidden — fill them in, then turn on “Shown on site”.
            </p>
          )}
        </div>
      </nav>

      <div className="pages-editor__preview">
        <PagePreview
          // Keyed by PAGE, not list: two lists on one page (drip menu +
          // services + FAQs on /services) share the iframe, so clicking
          // between them switches lists without reloading the preview.
          key={previewHref}
          href={previewHref}
          recordFields={recordFields}
          recordOrder={recordOrder}
          sectionFields={sectionFields}
          sectionOrder={sectionOrder}
          scrollTo={selectedSelector ?? undefined}
          selectedSelector={selectedSelector}
          hoverSelector={hoverSelector}
          onRecordInventory={(recs) => setInventoryByHref({ href: previewHref, recs: new Set(recs) })}
          onNavigate={onNavigate}
          notice={previewNotice()}
          onEditClick={(payload) => {
            // The whole preview is clickable: this list's card → open it;
            // another list's card → switch to that list with the card
            // open; nav/footer → Nav & footer; anything else → its
            // section on the page.
            if (payload.kind === 'chrome') {
              onOpenChrome();
              return;
            }
            if (payload.kind === 'record' && payload.record) {
              const i = payload.record.indexOf(':');
              const recList = payload.record.slice(0, i);
              const recId = payload.record.slice(i + 1);
              if (recList === list) selectRecord(recId, payload.field || undefined);
              else if ((LIST_KEYS as string[]).includes(recList)) onOpenList(recList as ListKey, recId);
              return;
            }
            if (payload.target) {
              onOpenSection(
                slugForHref(previewHref),
                payload.target,
                payload.kind === 'field' ? payload.field : undefined,
              );
            }
          }}
        />
      </div>

      <aside className="pages-editor__props" aria-hidden={!selected}>
        {renderPanel()}
      </aside>
    </div>
  );
}
